import * as Sentry from '@sentry/react';
import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useRef, useState} from 'react';

import {AuthService} from '~api/auth.service';
import {LandingPageRoutes, loginStatusQueryParam, replaceRoute, Route as RouteEnum} from '~utils/routeUtil';
import {useTrackEvent} from '~hooks/analytics.hooks';
import {fetchUser, fetchUserData} from '~redux/actions/userActions';
import {useAppDispatch} from '~redux/index';

import {Spinner} from '~components/common/Spinner';

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
});

export function AuthCallbackPage(): JSX.Element {
  const trackEvent = useTrackEvent();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (hasProcessedRef.current) {
      return;
    }

    const handleCallback = async () => {
      try {
        hasProcessedRef.current = true;

        // Check for token in URL hash first
        const fragment = window.location.hash.substring(1);
        const urlParams = new URLSearchParams(fragment);
        const token = urlParams.get('token');

        // If we're coming back from a logout flow AND there's no token, don't process
        // This prevents WorkOS from redirecting back without a token after logout
        // However, if there IS a token, this is a new login attempt, so clear the flag and proceed
        const logoutInProgress = sessionStorage.getItem('logout_in_progress');
        if (logoutInProgress === 'true' && !token) {
          sessionStorage.removeItem('logout_in_progress');
          // Ensure token is cleared (in case it was set before this check)
          if (AuthService.getToken()) {
            localStorage.removeItem('token');
          }
          // Clear the hash to remove any token from the URL
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
          // Use window.location.replace to ensure a full navigation that clears any token processing
          const indexUrl = new URL(RouteEnum.index, window.location.origin);
          indexUrl.searchParams.set(loginStatusQueryParam, 'logout');
          window.location.replace(indexUrl.toString());
          return;
        }

        // If there's a logout flag but we have a token, this is a new login - clear the flag
        if (logoutInProgress === 'true' && token) {
          sessionStorage.removeItem('logout_in_progress');
        }

        if (AuthService.isLoggedIn()) {
          await replaceRoute(RouteEnum.archive);
          return;
        }

        if (!token) {
          throw new Error('No token received');
        }

        AuthService.setToken(token);

        urlParams.delete('token');
        const newFragment = urlParams.toString();
        const newHash = newFragment ? `#${newFragment}` : '';
        window.history.replaceState({}, '', window.location.pathname + window.location.search + newHash);

        await dispatch(fetchUserData());
        const user = await fetchUser();

        trackEvent('login', {
          userAgent: navigator.userAgent,
          groupId: user?.email.endsWith('maddox.ai') ? `${user?.customer}_internal` : user?.customer,
        });

        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.identity) {
            Sentry.setUser({email: payload.identity});
          }
        } catch (e) {
          console.warn('Failed to decode JWT token for Sentry context', e);
        }

        const getDestinationFromLandingPage = (landingPage: string): string => {
          switch (landingPage.toUpperCase()) {
            case LandingPageRoutes.Monitor:
              return RouteEnum.monitor;
            case LandingPageRoutes.Tasks:
              return RouteEnum.tasks;
            case LandingPageRoutes.Archive:
              return RouteEnum.archive;
            case LandingPageRoutes.Models:
              return RouteEnum.models;
            default:
              return RouteEnum.archive;
          }
        };

        const destination =
          user?.landingPage && typeof user?.landingPage === 'string'
            ? getDestinationFromLandingPage(user.landingPage)
            : RouteEnum.archive;

        await replaceRoute(destination);
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');

        await replaceRoute(RouteEnum.index, {
          query: {
            error: 'auth_failed',
          },
        });
      }
    };

    handleCallback();

    return () => {
      // no-op cleanup
    };
  }, [dispatch, trackEvent]);

  if (error) {
    return <Spinner fullScreen />;
  }

  return <Spinner fullScreen />;
}

// eslint-disable-next-line import/no-default-export
export default AuthCallbackPage;
